import { useState } from 'react';
import { MailX, Plus, Trash2 } from 'lucide-react';
import {
  MAIL_SUPPRESSION_REASONS,
  type MailSuppressionEntry,
  type MailSuppressionReason,
} from '@peles/shared';
import { EntityList, FilterSelect, StatusBadge } from '../common';
import type { EntityListColumn } from '../common';
import { useDialog } from '../../context/DialogContext';
import {
  useAddMailSuppression,
  useMailSuppressions,
  useRemoveMailSuppression,
} from '../../hooks/useMailLog';
import { REASON_LABEL, formatWhen } from './mailLogFormat';

const REASON_OPTIONS = MAIL_SUPPRESSION_REASONS.map((id) => ({ id, label: REASON_LABEL[id] }));
const REASON_COLOR: Record<MailSuppressionReason, string> = {
  bounce: 'red',
  complaint: 'orange',
  manual: 'gray',
};

const PAGE_SIZE = 25;

/**
 * The addresses the application no longer emails. A hard bounce or a spam
 * complaint puts an address here on its own — mailing addresses that do not
 * exist or do not want the mail is how a sender ends up in everybody's spam
 * folder. An administrator adds one by hand (somebody asked to be left
 * alone), or releases one once it is fixed.
 */
export function MailSuppressionList() {
  const [search, setSearch] = useState('');
  const [reasons, setReasons] = useState<MailSuppressionReason[]>([]);
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useMailSuppressions({
    search: search.trim() || undefined,
    reasons,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const add = useAddMailSuppression();
  const remove = useRemoveMailSuppression();
  const { confirm, prompt, alert } = useDialog();

  async function addAddress() {
    const address = await prompt({
      title: 'Stop emailing an address',
      message: 'Nothing the application sends will go to this address until it is released.',
      placeholder: 'name@example.com',
      inputType: 'email',
      confirmLabel: 'Stop emailing',
    });
    if (!address?.trim()) return;
    try {
      await add.mutateAsync({ address: address.trim() });
    } catch (err) {
      await alert({
        title: 'That address could not be added',
        message: err instanceof Error ? err.message : 'Check it is a valid email address.',
        variant: 'error',
      });
    }
  }

  async function release(entry: MailSuppressionEntry) {
    const ok = await confirm({
      title: `Email ${entry.address} again?`,
      message:
        entry.reason === 'complaint'
          ? 'This address marked the application’s mail as spam. Emailing it again can land every message the application sends in spam folders.'
          : 'The next message to this address will be sent. If it bounces again, it is stopped again.',
      confirmLabel: 'Email again',
      variant: entry.reason === 'complaint' ? 'danger' : 'warning',
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(entry.address);
    } catch (err) {
      await alert({
        title: 'That address could not be released',
        message: err instanceof Error ? err.message : 'Please try again.',
        variant: 'error',
      });
    }
  }

  const columns: EntityListColumn<MailSuppressionEntry>[] = [
    {
      key: 'address',
      header: 'Address',
      render: (entry) => <span className="text-gray-900 break-all">{entry.address}</span>,
    },
    {
      key: 'reason',
      header: 'Why',
      render: (entry) => (
        <div className="space-y-0.5">
          <StatusBadge label={REASON_LABEL[entry.reason]} color={REASON_COLOR[entry.reason]} />
          {entry.detail && (
            <div className="text-xs text-gray-500 truncate" title={entry.detail}>
              {entry.detail}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Since',
      hideOnMobile: true,
      render: (entry) => (
        <span className="text-sm text-gray-600">{formatWhen(entry.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (entry) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void release(entry);
          }}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
          aria-label={`Email ${entry.address} again`}
        >
          <Trash2 size={12} />
          Release
        </button>
      ),
    },
  ];

  return (
    <EntityList
      items={data?.items ?? []}
      columns={columns}
      getKey={(entry) => entry.address}
      isLoading={isLoading}
      searchValue={search}
      onSearchChange={(value) => {
        setPage(1);
        setSearch(value);
      }}
      searchPlaceholder="Search addresses…"
      page={page}
      pageSize={PAGE_SIZE}
      total={data?.total ?? 0}
      onPageChange={setPage}
      emptyMessage={isError ? 'The list could not be loaded.' : 'No address is being held back.'}
      emptyIcon={<MailX className="w-8 h-8 text-gray-300" />}
      titleActions={
        <button
          type="button"
          onClick={addAddress}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          <Plus size={14} />
          Add address
        </button>
      }
      filterBar={
        <FilterSelect
          label="Why"
          items={REASON_OPTIONS}
          value={reasons}
          onChange={(value) => {
            setPage(1);
            setReasons(value as MailSuppressionReason[]);
          }}
          getValue={(option) => option.id}
          getLabel={(option) => option.label}
          multiple
        />
      }
    />
  );
}
